import { html, LitElement, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './speed-order-form-row.style.js'
import type { CSETextInput } from '@cloudsuite/mosaic-elements'


export class DeleteRowEvent extends Event {
  static readonly eventName = 'delete-row' as const

  constructor() {
    super(DeleteRowEvent.eventName, {
      bubbles: true,
      composed: true
    })
  }
}

export class ProductCodeInputEvent extends CustomEvent<{ value: string }> {
  static readonly eventName = 'product-code-input' as const

  constructor(public value: string) {
    super(ProductCodeInputEvent.eventName, {
      bubbles: true,
      composed: true,
      detail: { value }
    })
  }
}

export class QuantityChangeEvent extends CustomEvent<{ value: string }> {
  static readonly eventName = 'quantity-change' as const

  constructor(public value: string) {
    super(QuantityChangeEvent.eventName, {
      bubbles: true,
      composed: true,
      detail: { value }
    })
  }
}

export class RequestNewRowEvent extends Event {
  static readonly eventName = 'request-new-row' as const

  constructor() {
    super(RequestNewRowEvent.eventName, {
      bubbles: true,
      composed: true
    })
  }
}

@customElement('csc-speed-order-form-row')
export class CSCSpeedOrderFormRow extends LitElement {
  static override styles = [style]
  
  @property({ type: String, attribute: 'product-code' })
  productCode = ''

  @property({ type: String })
  quantity = ''

  @property({ type: String, attribute: 'product-name' })
  productName = ''

  @property({ type: Number })
  price = 0

  @property({ type: Boolean })
  filled = false

  @property({ type: Boolean, attribute: 'is-last' })
  isLast = false

  @property({ type: Boolean, attribute: 'is-searching' })
  isSearching = false

  @property({ type: Boolean, attribute: 'has-error' })
  hasError = false

  @property({ type: String, attribute: 'default-uom' })
  defaultUom = 'Per piece'

  @property({ type: String, attribute: 'price-per-price-uom' })
  pricePerPriceUom = ''

  @property({ type: String, attribute: 'price-uom' })
  priceUom = ''

  @property({ type: Number, attribute: 'order-minimum-qty' })
  orderMinimumQty = 1

  @property({ type: Number, attribute: 'order-default-qty' })
  orderDefaultQty = 1

  @property({ type: String, attribute: 'currency-symbol' })
  currencySymbol = '€€€'

  @query('cse-text-input[name="product-code"]')
  private productCodeInputEl?: CSETextInput

  @query('cse-text-input[name="quantity"]')
  private quantityInputEl?: CSETextInput

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties)

    if (changedProperties.has('hasError') && this.hasError) {
      this.focusInputEl(this.productCodeInputEl)
    } else if (changedProperties.has('filled') && this.filled) {
      this.focusInputEl(this.quantityInputEl)
    }
  }

  public focusProductCode(): void {
    this.focusInputEl(this.productCodeInputEl)
  }

  private focusInputEl(inputEl: CSETextInput | undefined): void {
    if (!inputEl) { return }
    inputEl.updateComplete.then(() => inputEl.focus())
  }

  private handleDeleteKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || event.shiftKey || !this.isLast) { return }

    event.preventDefault()
    this.dispatchEvent(new RequestNewRowEvent())
  }

  private handleDelete(): void {
    this.dispatchEvent(new DeleteRowEvent())
  }

  private handleProductCodeChange(e: Event) {
    const target = e.target as HTMLInputElement
    const value = target.value || ''
    this.productCode = value
    const event = new ProductCodeInputEvent(value)
    this.dispatchEvent(event)
  }

  private handleQuantityChange(e: Event) {
    const target = e.target as HTMLInputElement
    const value = target.value || ''
    this.quantity = value
    const event = new QuantityChangeEvent(value)
    this.dispatchEvent(event)
  }

  private renderInitialState(): TemplateResult {
    return html`
      <div class="row">
        <div class="product-code-input-container">
        <cse-form-field>
          <cse-text-input
            .placeholder=${t('product_code_placeholder', { ns: 'csc-speed-order-form-row', default: 'Product code' })}
            name="product-code"
            value=${this.productCode}
            ?clearable=${true}
            ?disabled=${this.isSearching}
            ?invalid=${this.hasError}
            @change=${this.handleProductCodeChange}
            
          ></cse-text-input>
          ${this.hasError ? html`
            <div slot="invalid-text" role="alert">${t('product_not_found_error', { ns: 'csc-speed-order-form-row', default: 'Product not found' })}</div>
          ` : ''}
          </cse-form-field>
          ${this.isSearching ? html`
            <div class="search-indicator">
              <cse-loader variant="default" animated></cse-loader>
            </div>
          ` : ''}
          
        </div>
        <cse-text-input
          class="quantity-input"
          type="number"
          value=${this.quantity}
          placeholder="1"
          name="quantity"
          @input=${this.handleQuantityChange}
        ></cse-text-input>
        <cse-icon-button variant="ghost" disabled>
          <cse-icon icon-name="trash-can" icon-style="light" aria-hidden="true"></cse-icon>
        </cse-icon-button>
      </div>
    `
  }

  private renderPrice(): TemplateResult {
    if (this.pricePerPriceUom && this.priceUom) {
      return html`
        <cse-price>${this.pricePerPriceUom}</cse-price>
        <cse-text variant="caption-l">${this.priceUom}</cse-text>
      `
    }

    return html`
      <cse-price>${this.currencySymbol} ${this.price.toFixed(2)}</cse-price>
      <cse-text variant="caption-l">${this.defaultUom}</cse-text>
    `
  }

  private renderFilledState(): TemplateResult {
    return html`
      <div class="row">
        <div class="product-info-container">
          <div class="product-info">
            <cse-text variant="product-title-m">${this.productName}</cse-text>
            <cse-text variant="caption-l">${this.productCode}</cse-text>
          </div>
          <div class="product-price">${this.renderPrice()}</div>
        </div>
        <cse-text-input
          class="quantity-input"
          type="number"
          value=${this.quantity}
          placeholder="1"
          name="quantity"
          min=${this.orderMinimumQty}
          step=${this.orderDefaultQty}
          @input=${this.handleQuantityChange}
        ></cse-text-input>
        <cse-icon-button
          variant="primary"
          id="delete-button"
          @click=${this.handleDelete}
          @keydown=${this.handleDeleteKeydown}
        >
          <cse-icon icon-name="trash-can" icon-style="light" aria-hidden="true"></cse-icon>
        </cse-icon-button>
  </div>
    `
  }

  override render(): TemplateResult {
    return html`${this.filled ? this.renderFilledState() : this.renderInitialState()}`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-speed-order-form-row': CSCSpeedOrderFormRow
  }

  interface HTMLElementEventMap {
    [DeleteRowEvent.eventName]: DeleteRowEvent
    [RequestNewRowEvent.eventName]: RequestNewRowEvent
    [ProductCodeInputEvent.eventName]: ProductCodeInputEvent
    [QuantityChangeEvent.eventName]: QuantityChangeEvent
  }
}
